import { supabase } from './supabaseClient';
import { LATEST_STAFF_APP_VERSION } from '../constants/appRelease';

// Export supabase for backward compatibility with admin pages
export { supabase };

// Admin Service - Web version
export class AdminService {
  static async getAdminStats() {
    try {
      const { data, error } = await supabase.rpc('admin_get_stats');
      
      if (error) {
        console.error('Failed to get admin stats:', error);
        return {
          totalUsers: 0,
          totalCustomers: 0,
          totalMechanics: 0,
          activeRequests: 0,
          totalRevenue: 0,
          pendingOrders: 0,
          supportTickets: 0,
          totalLeads: 0,
          totalMasterOrders: 0,
          pendingAssignments: 0,
          completedServices: 0,
          todayRequests: 0,
          deliveredLeads: 0,
          uniqueMechanicPincodes: 0,
        };
      }

      const statsRow = data?.[0];
      if (!statsRow) return null;

      return {
        totalUsers: Number(statsRow.total_users) || 0,
        totalCustomers: Number(statsRow.active_users) || 0,
        totalMechanics: 0,
        activeRequests: Number(statsRow.active_requests) || 0,
        totalRevenue: Number(statsRow.total_revenue) || 0,
        pendingOrders: Number(statsRow.pending_orders) || 0,
        supportTickets: Number(statsRow.support_tickets) || 0,
        totalLeads: Number(statsRow.total_leads) || 0,
        totalMasterOrders: Number(statsRow.total_master_orders) || 0,
        pendingAssignments: Number(statsRow.pending_assignments) || 0,
        completedServices: Number(statsRow.completed_services) || 0,
        todayRequests: 0,
        deliveredLeads: Number(statsRow.delivered_leads) || 0,
        uniqueMechanicPincodes: Number(statsRow.unique_mechanic_pincodes) || 0,
      };
    } catch (error) {
      console.error('Error getting admin stats:', error);
      return null;
    }
  }

  static async getSystemStatus() {
    try {
      const { error: dbError } = await supabase.from('users').select('id').limit(1);
      const database = dbError ? 'offline' : 'online';
      return {
        database,
        payments: 'online',
        notifications: 'online',
        chat: 'online',
      };
    } catch (error) {
      return {
        database: 'offline',
        payments: 'offline',
        notifications: 'offline',
        chat: 'offline',
      };
    }
  }

  static mapAdminUser(user) {
    return {
      id: user.id,
      name: user.name,
      email: user.email,
      phone: user.phone,
      roles: user.roles || [],
      status: user.status || 'active',
      created_at: user.created_at,
      last_login: user.last_login,
      team_lead_id: user.team_lead_id,
      team_lead_name: user.team_lead_name,
      avatar_url: user.avatar_url || null,
      dialer_user_id: user.dialer_user_id || null,
      app_version: user.app_version,
      app_build_number: user.app_build_number,
      app_platform: user.app_platform,
      app_flavor: user.app_flavor,
      app_version_reported_at: user.app_version_reported_at,
      plan_name: user.plan_name || null,
      membership_status: user.membership_status || null,
    };
  }

  static filterUsersLocally(users, {
    search = '',
    role = 'all',
    status = 'all',
    appVersion = 'all',
  } = {}) {
    const query = search.trim().toLowerCase();
    return (users || []).filter((user) => {
      const matchesSearch =
        !query ||
        user.name?.toLowerCase().includes(query) ||
        user.email?.toLowerCase().includes(query) ||
        user.phone?.includes(search.trim());

      const matchesRole =
        role === 'all' ||
        user.roles?.some((r) => r?.toLowerCase() === role.toLowerCase());

      const matchesStatus =
        status === 'all' ||
        (user.status || 'active').toLowerCase() === status.toLowerCase();

      const matchesAppVersion = (() => {
        if (appVersion === 'all') return true;
        if (appVersion === 'not_reported') return !user.app_version;
        if (appVersion === 'latest') return user.app_version === LATEST_STAFF_APP_VERSION;
        if (appVersion === 'outdated') {
          return Boolean(user.app_version) && user.app_version !== LATEST_STAFF_APP_VERSION;
        }
        return user.app_version === appVersion;
      })();

      return matchesSearch && matchesRole && matchesStatus && matchesAppVersion;
    });
  }

  static async listUsers({
    search = '',
    role = 'all',
    status = 'all',
    appVersion = 'all',
    page = 1,
    pageSize = 20,
  } = {}) {
    const emptyResult = {
      items: [],
      total: 0,
      page: 1,
      pageSize,
      totalPages: 1,
      stats: { total_users: 0, active_users: 0, inactive_users: 0, filtered_total: 0 },
      appVersions: [],
      usedFallback: false,
    };

    try {
      const { data, error } = await supabase.rpc('admin_list_users', {
        p_search: search,
        p_role: role,
        p_status: status,
        p_app_version: appVersion,
        p_latest_version: LATEST_STAFF_APP_VERSION,
        p_page: page,
        p_page_size: pageSize,
      });

      if (error) {
        const msg = String(error.message || '').toLowerCase();
        const rpcMissing =
          error.code === 'PGRST202' ||
          error.code === '42883' ||
          msg.includes('admin_list_users') ||
          msg.includes('could not find the function') ||
          msg.includes('404');

        if (!rpcMissing) {
          console.error('Error listing users:', error);
          return emptyResult;
        }

        console.warn('admin_list_users RPC missing, using fallback loader');
        const allUsers = (await AdminService.getAllUsers()).map(AdminService.mapAdminUser);
        const filtered = AdminService.filterUsersLocally(allUsers, { search, role, status, appVersion });
        const total = filtered.length;
        const totalPages = Math.max(1, Math.ceil(total / pageSize));
        const safePage = Math.min(Math.max(page, 1), totalPages);
        const start = (safePage - 1) * pageSize;
        const items = filtered.slice(start, start + pageSize);
        const appVersions = Array.from(
          new Set(allUsers.map((u) => u.app_version).filter(Boolean)),
        ).sort((a, b) => b.localeCompare(a, undefined, { numeric: true }));

        return {
          items,
          total,
          page: safePage,
          pageSize,
          totalPages,
          stats: {
            total_users: allUsers.length,
            active_users: allUsers.filter((u) => (u.status || 'active') === 'active').length,
            inactive_users: allUsers.filter((u) => (u.status || 'active') !== 'active').length,
            filtered_total: total,
          },
          appVersions,
          usedFallback: true,
        };
      }

      const items = Array.isArray(data?.items) ? data.items.map(AdminService.mapAdminUser) : [];

      return {
        items,
        total: Number(data?.total || 0),
        page: Number(data?.page || 1),
        pageSize: Number(data?.page_size || pageSize),
        totalPages: Math.max(Number(data?.total_pages || 1), 1),
        stats: {
          total_users: Number(data?.stats?.total_users || 0),
          active_users: Number(data?.stats?.active_users || 0),
          inactive_users: Number(data?.stats?.inactive_users || 0),
          filtered_total: Number(data?.stats?.filtered_total || 0),
        },
        appVersions: Array.isArray(data?.app_versions) ? data.app_versions : [],
        usedFallback: false,
      };
    } catch (error) {
      console.error('Error listing users:', error);
      return emptyResult;
    }
  }

  static async getTeamLeads({ status = 'all' } = {}) {
    const result = await AdminService.listUsers({
      role: 'teamlead',
      status,
      page: 1,
      pageSize: 500,
    });
    return (result.items || []).map(AdminService.mapAdminUser);
  }

  static async getTelecallers({ status = 'all' } = {}) {
    const result = await AdminService.listUsers({
      role: 'telecaller',
      status,
      page: 1,
      pageSize: 1000,
    });
    return (result.items || []).map(AdminService.mapAdminUser);
  }

  static async getStaffDirectory() {
    const [teamLeads, telecallers] = await Promise.all([
      AdminService.getTeamLeads(),
      AdminService.getTelecallers(),
    ]);
    const byId = new Map();
    [...teamLeads, ...telecallers].forEach((user) => {
      byId.set(String(user.id), user);
    });
    return { teamLeads, telecallers, byId };
  }

  static async getAllUsers() {
    try {
      const { data, error } = await supabase.rpc('admin_get_all_users_all');
      if (error) {
        console.error('Error getting users:', error);
        return [];
      }
      return data?.map(user => ({
        id: user.id,
        name: user.name,
        email: user.email,
        phone: user.phone,
        roles: user.roles || [],
        status: user.status || 'active',
        created_at: user.created_at,
        last_login: user.last_login,
        team_lead_id: user.team_lead_id,
        team_lead_name: user.team_lead_name,
        avatar_url: user.avatar_url || null,
        app_version: user.app_version,
        app_build_number: user.app_build_number,
        app_platform: user.app_platform,
        app_flavor: user.app_flavor,
        app_version_reported_at: user.app_version_reported_at,
      })) || [];
    } catch (error) {
      console.error('Error getting users:', error);
      return [];
    }
  }

  // Telecaller KYC list (joined view)
  static async getTelecallerKyc() {
    try {
      const { data, error } = await supabase.rpc('admin_list_telecaller_kyc');
      if (error) {
        console.error('Error getting telecaller KYC:', error);
        return [];
      }
      return data || [];
    } catch (error) {
      console.error('Error getting telecaller KYC:', error);
      return [];
    }
  }

  // Update telecaller KYC admin fields (verified + commission)
  static async updateTelecallerKyc(userId, { is_verified, commission_percent }) {
    try {
      const { data, error } = await supabase.rpc('admin_update_telecaller_kyc', {
        p_user_id: userId,
        p_is_verified: typeof is_verified === 'boolean' ? is_verified : null,
        p_commission_percent: commission_percent === '' || commission_percent === null || commission_percent === undefined
          ? null
          : Number(commission_percent),
      });
      if (error) throw error;
      return { success: true, data: data?.[0] || null };
    } catch (error) {
      console.error('Error updating telecaller KYC:', error);
      return { success: false, error: error.message || 'Failed to update KYC' };
    }
  }

  static async createUser(userData) {
    try {
      const primaryRole = userData.roles?.[0];
      if (!primaryRole) {
        return { success: false, error: 'At least one role is required' };
      }

      let assignedBy = userData.teamLeaderId || null;
      if (!assignedBy) {
        try {
          const currentUser = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
          assignedBy = currentUser?.id || null;
        } catch {
          assignedBy = null;
        }
      }

      // Plain password → admin_create_user_with_role RPC hashes with bcrypt on the server.
      const { data, error } = await supabase.rpc('admin_create_user_with_role', {
        p_name: userData.name,
        p_email: userData.email,
        p_phone: userData.phone || '',
        p_password: userData.password,
        p_role_name: primaryRole,
        p_status: userData.status || 'active',
        p_assigned_by: assignedBy,
        p_dialer_user_id: userData.dialerId || null,
      });

      if (error) throw error;

      const userId = data?.[0]?.id;
      if (!userId) throw new Error('Failed to get user ID');

      if (primaryRole === 'telecaller' && userData.teamLeaderId) {
        await this.changeTeamLead(userId, userData.teamLeaderId, assignedBy);
      }

      return { success: true, user: data[0] };
    } catch (error) {
      console.error('Error creating user:', error);
      return { success: false, error: error.message };
    }
  }

  static async updateUser(userId, userData) {
    try {
      await supabase.rpc('admin_update_user', {
        p_user_id: userId,
        p_name: userData.name || null,
        p_email: userData.email || null,
        p_phone: userData.phone || null,
        p_status: userData.status || null,
        p_dialer_user_id: userData.dialer_user_id || null
      });

      // Update roles if provided
      if (userData.roles && userData.roles.length > 0) {
        // Delete existing roles
        const { error: delRolesError } = await supabase.rpc('admin_delete_user_roles', {
          p_user_id: userId
        });
        if (delRolesError) throw delRolesError;

        // Get new role IDs
        const { data: rolesData } = await supabase.rpc('admin_get_roles_by_names', {
          role_names: userData.roles
        });

        if (rolesData) {
          // NOTE: In many deployments user_roles.user_id is UNIQUE (1 role per user).
          // Keep only the first role to avoid unique-constraint violations.
          const safeRoles = Array.isArray(rolesData) ? rolesData.slice(0, 1) : [];
          const roleAssignments = safeRoles.map(role => ({
            user_id: userId,
            role_id: role.id,
            assigned_by: userData.teamLeaderId || userId
          }));

          const { error: assignError } = await supabase.rpc('admin_assign_roles_to_user', {
            p_user_id: userId,
            // RPC expects jsonb, pass array directly (do NOT JSON.stringify)
            p_role_assignments: roleAssignments
          });
          if (assignError) throw assignError;
        }
      }

      return { success: true };
    } catch (error) {
      console.error('Error updating user:', error);
      return { success: false, error: error.message };
    }
  }

  static async deleteUser(userId) {
    try {
      await supabase.rpc('admin_update_user', {
        p_user_id: userId,
        p_status: 'inactive'
      });
      return { success: true };
    } catch (error) {
      console.error('Error deleting user:', error);
      return { success: false, error: error.message };
    }
  }

  static async resetUserPassword(email, newPassword) {
    try {
      if (!email || !newPassword) {
        return { success: false, error: 'Email and new password are required' };
      }
      if (newPassword.length < 6) {
        return { success: false, error: 'Password must be at least 6 characters' };
      }

      const cleanEmail = email.trim().toLowerCase();
      const cleanPassword = newPassword.trim();

      // Must use bcrypt RPC — same as staff login (login_verify_user + admin_create_user).
      // update_user_password on older DBs writes md5_* hashes and breaks login.
      const { data, error } = await supabase.rpc('admin_reset_user_password', {
        p_email: cleanEmail,
        p_new_password: cleanPassword,
      });

      if (error) {
        if (error.code === 'PGRST202') {
          return {
            success: false,
            error: 'Password reset RPC not deployed. Run web-app/database_migrations/admin_reset_user_password_bcrypt.sql in Supabase SQL Editor.',
          };
        }
        throw error;
      }

      const result = Array.isArray(data) ? data[0] : data;
      if (result?.success) {
        return { success: true, message: result.message || 'Password reset successfully' };
      }

      return { success: false, error: result?.message || 'Failed to reset password' };
    } catch (error) {
      console.error('Error resetting user password:', error);
      return { success: false, error: error.message || 'Failed to reset password' };
    }
  }

  static async changeTeamLead(telecallerId, newTeamLeadId, changedBy) {
    try {
      // DB function signatures vary across deployments; try most specific first.
      // Canonical (from repo SQL): admin_change_team_lead(telecaller_id, new_team_lead_id, changed_by, old_team_lead_id default null)
      const currentUser = (() => {
        try {
          return JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
        } catch {
          return {};
        }
      })();
      const changedByUuid = changedBy && String(changedBy).length > 20 ? changedBy : currentUser?.id || null;

      const attempts = [
        { telecaller_id: telecallerId, new_team_lead_id: newTeamLeadId, changed_by: changedByUuid, old_team_lead_id: null },
        { telecaller_id: telecallerId, new_team_lead_id: newTeamLeadId, old_team_lead_id: null },
        // legacy param names (older frontends)
        { p_telecaller_id: telecallerId, p_new_team_lead_id: newTeamLeadId, p_changed_by: changedByUuid },
      ];

      let lastError = null;
      for (const args of attempts) {
        const { error } = await supabase.rpc('admin_change_team_lead', args);
        if (!error) {
          return { success: true };
        }
        lastError = error;
        // stop early if it's not a signature mismatch
        if (error.code && error.code !== 'PGRST202') break;
      }

      if (lastError) throw lastError;
      return { success: true };
    } catch (error) {
      console.error('Error changing team lead:', error);
      return { success: false, error: error.message };
    }
  }

  static async getMechanicCoverageByState() {
    try {
      const { data, error } = await supabase.rpc('get_mechanic_coverage_by_state');
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting mechanic coverage:', error);
      return [];
    }
  }

  static async getUniqueMechanicPincodeCount() {
    try {
      const { data, error } = await supabase.rpc('get_unique_mechanic_pincode_count');
      if (error) throw error;
      return data?.[0]?.count || 0;
    } catch (error) {
      console.error('Error getting unique pincode count:', error);
      return 0;
    }
  }

  // Payout Requests (Admin)
  static async getPayoutRequests(status = null) {
    try {
      const { data, error } = await supabase.rpc('admin_list_payout_requests', {
        p_status: status,
      });
      if (error) {
        console.error('Error getting payout requests:', error);
        return [];
      }
      return data || [];
    } catch (error) {
      console.error('Error getting payout requests:', error);
      return [];
    }
  }

  static async getPayoutRequestItems(requestId) {
    try {
      const { data, error } = await supabase.rpc('admin_get_payout_request_items', {
        p_request_id: requestId,
      });
      if (error) {
        console.error('Error getting payout request items:', error);
        return [];
      }
      return data || [];
    } catch (error) {
      console.error('Error getting payout request items:', error);
      return [];
    }
  }

  static async updatePayoutRequest(requestId, status, note = null) {
    try {
      const { data, error } = await supabase.rpc('admin_update_payout_request', {
        p_request_id: requestId,
        p_status: status,
        p_note: note,
      });
      if (error) throw error;
      return { success: true, data: data?.[0] || null };
    } catch (error) {
      console.error('Error updating payout request:', error);
      return { success: false, error: error.message || 'Failed to update payout request' };
    }
  }

  // --- Call provider & DID (via SECURITY DEFINER RPCs) ---

  static _getAdminUserId() {
    try {
      const u = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
      return u.id || null;
    } catch { return null; }
  }

  static async getCallProviders() {
    const { data, error } = await supabase.rpc('admin_get_call_providers');
    if (error) {
      console.error('getCallProviders:', error);
      return [];
    }
    return data || [];
  }

  static async upsertCallProvider(row) {
    const uid = this._getAdminUserId();
    if (!uid) return { ok: false, error: 'Not logged in' };
    const { data, error } = await supabase.rpc('admin_upsert_call_provider', {
      p_user_id: uid,
      p_id: row.id ?? null,
      p_name: row.name,
      p_display_name: row.display_name,
      p_api_base_url: row.api_base_url ?? '',
      p_api_endpoint: row.api_endpoint ?? '',
      p_http_method: row.http_method ?? 'POST',
      p_auth_type: row.auth_type ?? 'query',
      p_auth_creds: row.auth_credentials ?? {},
      p_request_map: row.request_mapping ?? {},
      p_resp_path: row.response_success_path ?? null,
      p_has_did: row.has_did_support ?? false,
      p_is_enabled: row.is_enabled ?? true,
      p_priority: row.priority ?? 10,
    });
    if (error) return { ok: false, error: error.message };
    return data || { ok: true };
  }

  static async deleteCallProvider(id) {
    const uid = this._getAdminUserId();
    if (!uid) return { ok: false, error: 'Not logged in' };
    const { data, error } = await supabase.rpc('admin_delete_call_provider', { p_user_id: uid, p_id: id });
    if (error) return { ok: false, error: error.message };
    return data || { ok: true };
  }

  static async updateCallProviderPriority(id, priority) {
    const uid = this._getAdminUserId();
    if (!uid) return { ok: false, error: 'Not logged in' };
    const { data, error } = await supabase.rpc('admin_update_call_provider_priority', {
      p_user_id: uid, p_id: id, p_priority: priority,
    });
    if (error) return { ok: false, error: error.message };
    return data || { ok: true };
  }

  static async getCallProviderConfig() {
    const { data, error } = await supabase.rpc('admin_get_call_provider_config');
    if (error) {
      console.error('getCallProviderConfig:', error);
      return null;
    }
    const rows = data || [];
    return rows.length > 0 ? rows[0] : null;
  }

  static async updateCallProviderConfig(patch) {
    const uid = this._getAdminUserId();
    if (!uid) return { ok: false, error: 'Not logged in' };
    const { data, error } = await supabase.rpc('admin_update_call_provider_config', {
      p_user_id: uid,
      p_fallback_enabled: patch.fallback_enabled ?? null,
      p_did_rotation_mode: patch.smartflo_did_rotation_mode ?? null,
    });
    if (error) return { ok: false, error: error.message };
    return data || { ok: true };
  }

  static async getProviderDids(providerId) {
    const { data, error } = await supabase.rpc('admin_get_provider_dids', { p_provider_id: providerId });
    if (error) {
      console.error('getProviderDids:', error);
      return [];
    }
    return data || [];
  }

  static async upsertDid(row) {
    const uid = this._getAdminUserId();
    if (!uid) return { ok: false, error: 'Not logged in' };
    const { data, error } = await supabase.rpc('admin_upsert_did', {
      p_user_id: uid,
      p_id: row.id ?? null,
      p_provider_id: row.provider_id,
      p_did_number: row.did_number,
      p_label: row.label ?? null,
      p_is_active: row.is_active ?? true,
      p_priority: row.priority ?? 1,
    });
    if (error) return { ok: false, error: error.message };
    return data || { ok: true };
  }

  static async deleteDid(id) {
    const uid = this._getAdminUserId();
    if (!uid) return { ok: false, error: 'Not logged in' };
    const { data, error } = await supabase.rpc('admin_delete_did', { p_user_id: uid, p_id: id });
    if (error) return { ok: false, error: error.message };
    return data || { ok: true };
  }

  static async getAgentDidAssignments() {
    const { data, error } = await supabase.rpc('admin_get_agent_did_assignments');
    if (error) {
      console.error('getAgentDidAssignments:', error);
      return [];
    }
    return data || [];
  }

  static async upsertAgentDidAssignment(row) {
    const uid = this._getAdminUserId();
    if (!uid) return { ok: false, error: 'Not logged in' };
    const { data, error } = await supabase.rpc('admin_upsert_agent_did_assignment', {
      p_user_id: uid,
      p_agent_phone: row.agent_phone,
      p_agent_name: row.agent_name ?? null,
      p_did_id: row.did_id ?? null,
      p_is_active: row.is_active ?? true,
    });
    if (error) return { ok: false, error: error.message };
    return data || { ok: true };
  }

  static async getAppEnquiries(type, { search = '', limit = 50, offset = 0 } = {}) {
    try {
      const { data, error } = await supabase.rpc('admin_list_app_enquiries', {
        p_type: type,
        p_limit: limit,
        p_offset: offset,
        p_search: search || null,
      });
      if (error) {
        console.error('getAppEnquiries:', error);
        return { success: false, rows: [], total: 0, error: error.message };
      }
      return {
        success: data?.success !== false,
        rows: data?.rows || [],
        total: Number(data?.total) || 0,
        error: data?.error || null,
      };
    } catch (error) {
      console.error('getAppEnquiries:', error);
      return { success: false, rows: [], total: 0, error: error.message };
    }
  }

  static async updateRsaEnquiryStatus(requestId, action, note = null) {
    try {
      const { data, error } = await supabase.rpc('admin_update_rsa_enquiry_status', {
        p_request_id: requestId,
        p_action: action,
        p_note: note || null,
      });
      if (error) {
        console.error('updateRsaEnquiryStatus:', error);
        return { success: false, error: error.message };
      }
      return {
        success: data?.success !== false,
        error: data?.error || null,
        data,
      };
    } catch (error) {
      console.error('updateRsaEnquiryStatus:', error);
      return { success: false, error: error.message };
    }
  }

  static async updateCarLoanEnquiryStatus(leadId, action, note = null) {
    try {
      const { data, error } = await supabase.rpc('admin_update_car_loan_enquiry_status', {
        p_lead_id: leadId,
        p_action: action,
        p_note: note || null,
      });
      if (error) {
        console.error('updateCarLoanEnquiryStatus:', error);
        return { success: false, error: error.message };
      }
      return {
        success: data?.success !== false,
        error: data?.error || null,
        data,
      };
    } catch (error) {
      console.error('updateCarLoanEnquiryStatus:', error);
      return { success: false, error: error.message };
    }
  }

  static async updateCarServiceEnquiryStatus(bookingId, action, note = null) {
    try {
      const { data, error } = await supabase.rpc('admin_update_car_service_enquiry_status', {
        p_booking_id: bookingId,
        p_action: action,
        p_note: note || null,
      });
      if (error) {
        console.error('updateCarServiceEnquiryStatus:', error);
        return { success: false, error: error.message };
      }
      return {
        success: data?.success !== false,
        error: data?.error || null,
        data,
      };
    } catch (error) {
      console.error('updateCarServiceEnquiryStatus:', error);
      return { success: false, error: error.message };
    }
  }
}
